import { type MergedCoupon } from '@cart-services/types'
import { sortCoupons } from '@cart-services/utils/sortCoupons'
import { useCallbackEvent } from '@dni-react-hooks/use-callback-event'
import { CouponType, UsageMethod } from '@hooks-api/types/dataModel/coupon'
import { type TabListProps } from '@pets-ui/TabList/TabList'
import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import cartUiI18nInstance from '../i18n/i18nConfig'
import { type Category, type CouponCategoriesProps } from './CouponCategories'

export type CouponCategory = CouponType | 'all'
export enum CouponTabValue {
  AUTO_COUPON = 'autoCoupon',
  MANUAL_INPUT = 'manualInput',
}

export const useCouponCategories = (coupons: MergedCoupon[] | undefined) => {
  const [tabValue, setTabValue] = useState<CouponTabValue>(CouponTabValue.AUTO_COUPON)
  const { t } = useTranslation('cartUi', { i18n: cartUiI18nInstance })
  const {
    manualInputCoupons,
    autoCouponCoupons,
    allProductCoupons,
    specificProductCoupons,
    warehouseProductCoupons,
    disShippingCoupons,
  } = useMemo(() => {
    const manualInputCoupons: MergedCoupon[] = []
    const autoCouponCoupons: MergedCoupon[] = []
    const allProductCoupons: MergedCoupon[] = []
    const specificProductCoupons: MergedCoupon[] = []
    const warehouseProductCoupons: MergedCoupon[] = []
    const disShippingCoupons: MergedCoupon[] = []

    coupons?.forEach((coupon) => {
      if (coupon.usage_method === UsageMethod.MANUAL) {
        manualInputCoupons.push(coupon)
      }
      if (coupon.usage_method === UsageMethod.AUTO) {
        autoCouponCoupons.push(coupon)
      }
      if (coupon.couponTypes.has(CouponType.ALL_PRODUCT)) {
        allProductCoupons.push(coupon)
      }
      if (coupon.couponTypes.has(CouponType.SPECIFIC_PRODUCT)) {
        specificProductCoupons.push(coupon)
      }
      if (coupon.couponTypes.has(CouponType.WAREHOUSE_PRODUCT)) {
        warehouseProductCoupons.push(coupon)
      }
      if (coupon.couponTypes.has(CouponType.DIS_SHIPPING)) {
        disShippingCoupons.push(coupon)
      }
    })

    return {
      manualInputCoupons: sortCoupons(manualInputCoupons),
      autoCouponCoupons: sortCoupons(autoCouponCoupons),
      specificProductCoupons: sortCoupons(specificProductCoupons),
      allProductCoupons: sortCoupons(allProductCoupons),
      warehouseProductCoupons: sortCoupons(warehouseProductCoupons),
      disShippingCoupons: sortCoupons(disShippingCoupons),
    }
  }, [coupons])

  const categories = useMemo(() => {
    const categories: Category<CouponCategory>[] = [{ value: 'all' as CouponCategory, label: t('cartUi.all', { defaultValue: '全部' }) }]
    if (specificProductCoupons && specificProductCoupons.length > 0) {
      categories.push({ value: CouponType.SPECIFIC_PRODUCT, label: t('cartUi.specificProducts', { defaultValue: '指定商品' }) })
    }
    if (allProductCoupons && allProductCoupons.length > 0) {
      categories.push({ value: CouponType.ALL_PRODUCT, label: t('cartUi.allProducts', { defaultValue: '全館商品' }) })
    }
    if (warehouseProductCoupons && warehouseProductCoupons.length > 0) {
      categories.push({ value: CouponType.WAREHOUSE_PRODUCT, label: t('cartUi.warehousedProducts', { defaultValue: '寄倉商品' }) })
    }
    if (disShippingCoupons && disShippingCoupons.length > 0) {
      categories.push({ value: CouponType.DIS_SHIPPING, label: t('cartUi.shippingDiscount', { defaultValue: '運費折扣' }) })
    }
    return categories
  }, [t, specificProductCoupons, allProductCoupons, warehouseProductCoupons, disShippingCoupons])

  const [selectedCategory, setSelectedCategory] = useState<CouponCategory>(categories[0]?.value ?? 'all')

  const availableCouponsForList = useMemo(() => {
    if (tabValue === CouponTabValue.MANUAL_INPUT) return manualInputCoupons
    if (selectedCategory === 'all') return autoCouponCoupons
    if (selectedCategory === CouponType.SPECIFIC_PRODUCT) return specificProductCoupons
    if (selectedCategory === CouponType.ALL_PRODUCT) return allProductCoupons
    if (selectedCategory === CouponType.WAREHOUSE_PRODUCT) return warehouseProductCoupons
    if (selectedCategory === CouponType.DIS_SHIPPING) return disShippingCoupons
    return undefined
  }, [tabValue, selectedCategory, manualInputCoupons, autoCouponCoupons, specificProductCoupons, allProductCoupons, warehouseProductCoupons, disShippingCoupons])

  const handleChangeCategory: CouponCategoriesProps<CouponCategory>['onChange'] = useCallbackEvent((value) => {
    setSelectedCategory(value)
  })

  const handleTabChange: TabListProps<CouponTabValue>['onChange'] = useCallbackEvent((_, newValue) => {
    setTabValue(newValue ?? CouponTabValue.AUTO_COUPON)
  })

  return {
    categories,
    selectedCategory,
    setSelectedCategory,
    availableCouponsForList,
    handleChangeCategory,
    handleTabChange,
    tabValue,
  }
}
